import React, { useState, useEffect } from 'react';
import '../../styles/index.css';
import SubtextGame from '../../games/subtext/SubtextGame.tsx';
import WeaverGame from '../../games/weaver/WeaverGame.tsx';
import DailyGameContainer from '../GameContainer/DailyGameContainer.tsx';
import GameStatsModal from '../modals/GameStatsModal/GameStatsModal.tsx';
import HowToPlayModal from '../HowToPlayModal/HowToPlayModal.tsx';
import YesterdayModal from '../modals/YesterdayModal/YesterdayModal.tsx';
import {getHasSolvedGameNumber} from '../../utils/userStatsUtils.ts';
import {
  logClickHowToPlay,
  logClickYesterday,
  logClickUserStats
} from '../../utils/loggingUtils.ts';
import useGameNavigation from '../../utils/useGameNavigation.ts';
import { useNavigate } from 'react-router-dom';
import { GameType } from '../../types/GameType.ts';
import { GameDataWithDictionary, getGameConfig, WeaverGameConfig } from '../../types/GameConfig.ts';
import { getDateFromGameNumber } from '../../utils/archiveUtils.ts';
import { shouldShowHowToPlayModal, setHasShownHowToPlay } from '../../utils/userStatsUtils.ts';
import { WordPair } from '../../utils/wordPairUtils.ts';
import { SubtextSingleGameData } from '../../utils/subtextGameData.ts';
import { nullthrows } from '../../utils/assertionUtils.ts';
import { useNotification } from '../GameContainer/NotificationContext.tsx';
import { setHasLoadedWeaverX, setHasLoadedSubtext } from '../../utils/userStatsUtils.ts';


interface DailyGameProps {
  gameNumber: number | null;
  isArchive: boolean;
  gameType: GameType;
}



export default function DailyGame(props: DailyGameProps) {
  const { 
    gameNumber: propGameNumber,
    isArchive = false,
    gameType,
  } = props;

  const gameConfig = getGameConfig(gameType);
  const gameNumber = propGameNumber ?? gameConfig.getCurrentGameNumber();
  const gameData = gameConfig.getGameData(gameNumber);
  
  const hasSolvedToday = getHasSolvedGameNumber(gameType, gameNumber);
  const isFirstGame = gameNumber === 1;

  const [isHowToPlayModalOpen, setIsHowToPlayModalOpen] = useState(shouldShowHowToPlayModal(gameType));
  const [isYesterdayModalOpen, setIsYesterdayModalOpen] = useState(false);
  const [isGameStatsModalOpen, setIsGameStatsModalOpen] = useState(hasSolvedToday);

  const gameNavigation = useGameNavigation(gameType);
  const routerNavigate = useNavigate();
  const { setShowWeaverXLaunchNotification, setShowSubtextLaunchNotification } = useNotification();

  // Mark games as loaded when they're accessed
  useEffect(() => {
    if (gameType === GameType.WEAVER_X) {
      setHasLoadedWeaverX();
      setShowWeaverXLaunchNotification(false);
    }
    if (gameType === GameType.SUBTEXT) {
      setHasLoadedSubtext();
      setShowSubtextLaunchNotification(false);
    }
  }, [gameType, setShowWeaverXLaunchNotification, setShowSubtextLaunchNotification]);

  // Check for game number changes when returning to tab
  useEffect(() => {
    const checkForNewGame = () => {
      const currentGameNumber = propGameNumber ?? gameConfig.getCurrentGameNumber();
      if (currentGameNumber !== gameNumber) {
        // Game number has changed, force a page reload to get fresh data
        window.location.reload();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForNewGame();
      }
    };

    const handleFocus = () => {
      checkForNewGame();
    };

    // Only add listeners if this is the current game (not an archived game)
    if (!isArchive && propGameNumber === null) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [gameNumber, propGameNumber, gameConfig, isArchive]);

  // Update HowToPlay modal state when gameType changes
  useEffect(() => {
    setIsHowToPlayModalOpen(shouldShowHowToPlayModal(gameType));
  }, [gameType]);

  // Update game-specific state when gameType changes
  useEffect(() => {
    // Reset yesterday modal when switching game types
    setIsYesterdayModalOpen(false);
    
    // Update game stats modal based on whether user has solved current game
    const currentGameNumber = propGameNumber ?? gameConfig.getCurrentGameNumber();
    const hasSolvedCurrentGame = getHasSolvedGameNumber(gameType, currentGameNumber);
    setIsGameStatsModalOpen(hasSolvedCurrentGame);
  }, [gameType, propGameNumber, gameConfig]);

  return (
    <DailyGameContainer
      gameType={gameType}
      gameNumber={gameNumber}
      isArchive={isArchive}
      onHowToPlayClick={() => {
        logClickHowToPlay(gameType, gameNumber);
        setIsHowToPlayModalOpen(true);
      }}
      onYesterdayClick={() => {
        logClickYesterday(gameType, gameNumber);
        setIsYesterdayModalOpen(true);
      }}
      onUserStatsClick={() => {
        logClickUserStats(gameType, gameNumber);
        setIsGameStatsModalOpen(true);
      }}
    >
      {(() => {
        // Check if game data is available
        if (!gameData) {
          return (
            <div className="refresh-message">
              <div className="refresh-content">
                <h2>🔄 New games available!</h2>
                <p>Please refresh your browser to get the latest games.</p>
                <button 
                  className="refresh-button"
                  onClick={() => window.location.reload()}
                >
                  Refresh Now
                </button>
              </div>
            </div>
          );
        }

        // For Weaver games
        if ((gameType === GameType.WEAVER || gameType === GameType.WEAVER_X) && gameData) {
          const gameDataWithWordPair = gameData as WordPair;
          const gameDataWithDict = gameData as GameDataWithDictionary;
          const dictionaryConfig = gameDataWithDict?.dictionary || nullthrows(gameConfig.defaultDictionaryConfig);
          
          return (
            <WeaverGame
              key={`${gameType}-${gameNumber}`} // 🔑 KEY PROP - forces remount on game change
              startWord={gameDataWithWordPair.startWord}
              endWord={gameDataWithWordPair.endWord}
              gameNumber={gameNumber}
              gameType={gameType}
              onGameLoad={() => gameData && setSpecialMessageToast(gameType, gameNumber)}
              openGameStatsModal={() => setIsGameStatsModalOpen(true)}
              setErrorToast={(message) => setErrorToast(message)}
              setSuccessToast={(message) => setSuccessToast(message)}
              dictionaryConfig={dictionaryConfig}
            />
          );
        }
        
        // For Subtext games
        if (gameType === GameType.SUBTEXT && gameData) {
          const subtextGameData = gameData as SubtextSingleGameData;
          
          return (
            <SubtextGame
              key={`${gameType}-${gameNumber}`}
              dailyWord={subtextGameData.word}
              realisticMaxScore={subtextGameData.realisticMaxScore}
              longestSubWordLength={subtextGameData.longestSubWordLength}
              validSubWords={subtextGameData.validSubWords}
              gameNumber={gameNumber}
              gameType={gameType}
              onGameLoad={() => {}}
              openGameStatsModal={() => setIsGameStatsModalOpen(true)}
              setErrorToast={(message) => setErrorToast(message)}
              setSuccessToast={(message) => setSuccessToast(message)}
            />
          );
        }
        
        // Fallback for unknown game types
        return (
          <div className="refresh-message">
            <div className="refresh-content">
              <h2>⚠️ Unknown game type</h2>
              <p>This game type is not supported.</p>
            </div>
          </div>
        );
      })()}
      <HowToPlayModal
        gameType={gameType}
        isOpen={isHowToPlayModalOpen}
        onClose={() => {
          setHasShownHowToPlay(gameType);
          setIsHowToPlayModalOpen(false);
        }}
      />
      {!isFirstGame && <YesterdayModal
        isOpen={isYesterdayModalOpen}
        onClose={() => setIsYesterdayModalOpen(false)}
          gameType={gameType}
        />
      }
      <GameStatsModal
        gameNumber={gameNumber}
        gameType={gameType}
        isOpen={isGameStatsModalOpen}
        onClose={() => setIsGameStatsModalOpen(false)}
        onCopyToClipboard={() => setGenericToast('Copied to clipboard')}
        onPlayNext={() => {
          setIsGameStatsModalOpen(false);
          const nextGameNumber = gameNumber + 1;
          if (gameConfig.isValidGameNumber(nextGameNumber) && !gameConfig.isGameNumberCurrentGame(nextGameNumber)) {
            const basePath = gameConfig.routePath.endsWith('/')
              ? gameConfig.routePath.slice(0, -1)
              : gameConfig.routePath;
            routerNavigate(`${basePath}/game/${nextGameNumber}`);
          } else {
            gameNavigation.redirectToTodayGame('game_stats_modal');
          }
        }}
        onReturnToArchive={() => {
          setIsGameStatsModalOpen(false);
          const { year, month } = getDateFromGameNumber(gameType, gameNumber);
          const basePath = gameConfig.routePath.endsWith('/')
            ? gameConfig.routePath.slice(0, -1)
            : gameConfig.routePath;
          routerNavigate(`${basePath}/archive?year=${year}&month=${month}`);
        }}
      />
      <div className='toast aboveKeyboardToast' id='error-toast' />
      <div className='toast aboveKeyboardToast' id='success-toast' />
      <div className='toast belowHeaderToast' id='generic-toast' />
      <div className='toast belowHeaderToast' id='special-message-toast' />
    </DailyGameContainer>
  );
}

function setErrorToast(message: string): void {
  setToast('error-toast', message, 2000);
}

function setSuccessToast(message: string): void {
  setToast('success-toast', message, 2000);
}

function setGenericToast(message: string): void {
  setToast('generic-toast', message, 2000);
}

function setSpecialMessageToast(gameType: GameType, gameNumber: number): void {
  // Only Weaver games have special messages
  if (gameType !== GameType.WEAVER && gameType !== GameType.WEAVER_X) {
    return;
  }
  
  const gameConfig = getGameConfig(gameType) as WeaverGameConfig;
  const [specialMessage, specialMessageBackgroundColor] = gameConfig.getSpecialMessageData(gameNumber);
  if (specialMessage != null) {
    if (specialMessageBackgroundColor != null) {
      const specialMessageToastElement = document.getElementById('special-message-toast');
      if (specialMessageToastElement) {
        specialMessageToastElement.style.setProperty('background-color', specialMessageBackgroundColor);
      }
    }
    setToast('special-message-toast', specialMessage, 3000);
  }
}

function setToast(elementId: string, message: string, duration: number): void {
  const element = document.getElementById(elementId);
  if (!element) return;
  element.innerText = message;
  element.style.display = 'block';
  setTimeout(() => {
    element.style.display = 'none';
    element.innerText = '';
  }, duration);
}
